// Application state: authentication, the in-memory graph (held as core domain
// types so the shared engine can recompute instantly), the month context, and
// the theme. Mutations update local state at once for a responsive canvas and
// persist to the API in the background.
import { canConnect, type ComputeContext, type Flow, type VulpariNode } from "@vulpari/core";
import { create } from "zustand";

import {
  api,
  getToken,
  setToken,
  type AuthUser,
  type FlowPatch,
  type FlowRow,
  type Group,
  type Ledger,
  type LedgerPatch,
  type NodePatch,
  type NodeRow,
} from "./api/client.js";
import { defaultMeta, defaultName } from "./lib/defaults.js";
import { cachedRates, fetchRates } from "./lib/fx.js";

type Theme = "dark" | "light";
type Status = "loading" | "anonymous" | "ready";

/** A pending grouping action awaiting the user's confirmation. Nothing groups
 * automatically; the user always confirms first. */
export type GroupPrompt =
  | { kind: "add"; nodeId: string; nodeName: string; groupId: string; groupName: string }
  | { kind: "create"; nodeIds: string[]; nodeNames: [string, string] }
  | null;

// Group tints, cycled as groups are created. Theme tokens, so they adapt.
export const GROUP_COLOURS = [
  "var(--colour-neutral)",
  "var(--colour-positive)",
  "var(--colour-accent)",
  "var(--colour-warning)",
];

function toCoreNode(row: NodeRow): VulpariNode {
  return {
    id: row.id,
    type: row.type,
    name: row.name,
    currency: row.currency,
    positionX: row.positionX,
    positionY: row.positionY,
    meta: row.meta,
  } as VulpariNode;
}

function toCoreFlow(row: FlowRow): Flow {
  return {
    id: row.id,
    sourceNodeId: row.sourceNodeId,
    targetNodeId: row.targetNodeId,
    amountType: row.amountType,
    amountValue: row.amountValue,
    schedule: row.schedule,
    label: row.label,
  };
}

function initialTheme(): Theme {
  const stored = localStorage.getItem("vulpari.theme");
  return stored === "light" ? "light" : "dark";
}

function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  localStorage.setItem("vulpari.theme", theme);
}

interface AppState {
  status: Status;
  user: AuthUser | null;
  nodes: VulpariNode[];
  flows: Flow[];
  groups: Group[];
  ledgers: Ledger[];
  currentLedgerId: string | null;
  context: ComputeContext;
  theme: Theme;
  error: string | null;
  notice: string | null;
  selectedNodeIds: string[];
  selectedEdgeIds: string[];
  view: "canvas" | "runway" | "timeline";
  horizon: number;

  init: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loadWorkspace: () => Promise<void>;
  loadGraph: () => Promise<void>;
  loadDemo: () => Promise<void>;
  switchLedger: (id: string) => Promise<void>;
  createLedger: (name: string) => Promise<void>;
  renameLedger: (id: string, name: string) => Promise<void>;
  updateLedgerSettings: (patch: LedgerPatch) => Promise<void>;
  deleteLedger: (id: string) => Promise<void>;
  setDefaultLedger: (id: string) => Promise<void>;
  moveNode: (id: string, x: number, y: number) => void;
  connect: (sourceNodeId: string, targetNodeId: string) => Promise<void>;
  removeFlow: (id: string) => Promise<void>;
  setNotice: (notice: string | null) => void;
  setSelection: (nodeIds: string[], edgeIds: string[]) => void;
  selectNode: (id: string | null) => void;
  selectEdge: (id: string | null) => void;
  addNode: (type: VulpariNode["type"]) => Promise<void>;
  updateNode: (id: string, patch: NodePatch) => void;
  togglePaid: (id: string) => void;
  deleteNode: (id: string) => Promise<void>;
  deleteNodes: (ids: string[]) => Promise<void>;
  createGroup: (name: string, nodeIds: string[]) => Promise<void>;
  renameGroup: (id: string, name: string) => Promise<void>;
  recolourGroup: (id: string, color: string) => Promise<void>;
  addToGroup: (groupId: string, nodeIds: string[]) => Promise<void>;
  removeFromGroup: (nodeId: string) => Promise<void>;
  toggleCollapse: (id: string) => Promise<void>;
  ungroup: (id: string) => Promise<void>;
  groupPrompt: GroupPrompt;
  setGroupPrompt: (prompt: GroupPrompt) => void;
  updateFlow: (id: string, patch: FlowPatch) => void;
  setMonth: (month: string) => void;
  setView: (view: "canvas" | "runway" | "timeline") => void;
  setHorizon: (horizon: number) => void;
  loadRates: () => Promise<void>;
  toggleTheme: () => void;
  updateEmail: (email: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

// Debounce timers for persisting node positions while dragging.
const positionTimers = new Map<string, ReturnType<typeof setTimeout>>();
// Debounce timers for persisting node edits (name, currency, meta).
const editTimers = new Map<string, ReturnType<typeof setTimeout>>();
// Debounce timers for persisting flow edits.
const flowTimers = new Map<string, ReturnType<typeof setTimeout>>();

export const useStore = create<AppState>((set, get) => ({
  status: "loading",
  user: null,
  nodes: [],
  flows: [],
  groups: [],
  ledgers: [],
  currentLedgerId: null,
  context: { month: currentMonth() },
  theme: initialTheme(),
  error: null,
  notice: null,
  groupPrompt: null,
  selectedNodeIds: [],
  selectedEdgeIds: [],
  view: "canvas",
  horizon: 12,

  async init() {
    applyTheme(get().theme);
    if (!getToken()) {
      set({ status: "anonymous" });
      return;
    }
    try {
      const { user } = await api.me();
      set({ user });
      await get().loadWorkspace();
      set({ status: "ready" });
    } catch {
      setToken(null);
      set({ status: "anonymous" });
    }
  },

  async login(email, password) {
    const user = await api.login(email, password);
    set({ user, error: null });
    await get().loadWorkspace();
    set({ status: "ready" });
  },

  async register(email, password) {
    const user = await api.register(email, password);
    set({ user, error: null });
    await get().loadWorkspace();
    set({ status: "ready" });
  },

  logout() {
    setToken(null);
    set({
      status: "anonymous",
      user: null,
      nodes: [],
      flows: [],
      ledgers: [],
      currentLedgerId: null,
    });
  },

  async loadWorkspace() {
    const { ledgers } = await api.listLedgers();
    const current = get().currentLedgerId;
    const defaultId = get().user?.defaultLedgerId;
    const exists = (id: string | null | undefined) =>
      Boolean(id) && ledgers.some((ledger) => ledger.id === id);

    // Keep the current ledger if it still exists, else the user's default, else
    // the oldest ledger.
    const nextId = exists(current)
      ? current
      : exists(defaultId)
        ? defaultId!
        : (ledgers[0]?.id ?? null);

    const currentLedger = ledgers.find((ledger) => ledger.id === nextId);
    set((state) => ({
      ledgers,
      currentLedgerId: nextId,
      context: {
        ...state.context,
        primaryCurrency: currentLedger?.currency ?? "GBP",
        // Open the canvas on the ledger's opening-balance month when it has one.
        ...(currentLedger?.startMonth ? { month: currentLedger.startMonth } : {}),
      },
    }));
    if (nextId) {
      await get().loadGraph();
    }
    void get().loadRates();
  },

  async loadGraph() {
    const ledgerId = get().currentLedgerId;
    if (!ledgerId) {
      set({ nodes: [], flows: [], groups: [] });
      return;
    }
    // Load whatever is in the ledger. New ledgers stay blank until the user
    // adds nodes or chooses to load the demo data.
    const graph = await api.graph(ledgerId);
    set({
      nodes: graph.nodes.map(toCoreNode),
      flows: graph.flows.map(toCoreFlow),
      groups: graph.groups,
      selectedNodeIds: [],
      selectedEdgeIds: [],
    });
  },

  async loadDemo() {
    const ledgerId = get().currentLedgerId;
    if (!ledgerId) {
      return;
    }
    await api.seed(ledgerId).catch(() => undefined);
    await get().loadGraph();
  },

  async switchLedger(id) {
    if (id === get().currentLedgerId) {
      return;
    }
    const ledger = get().ledgers.find((entry) => entry.id === id);
    set((state) => ({
      currentLedgerId: id,
      context: {
        ...state.context,
        primaryCurrency: ledger?.currency ?? "GBP",
        ...(ledger?.startMonth ? { month: ledger.startMonth } : {}),
      },
    }));
    await get().loadGraph();
    void get().loadRates();
  },

  async createLedger(name) {
    const ledger = await api.createLedger(name);
    set((state) => ({
      ledgers: [...state.ledgers, ledger],
      currentLedgerId: ledger.id,
      context: { ...state.context, primaryCurrency: ledger.currency },
    }));
    await get().loadGraph();
  },

  async renameLedger(id, name) {
    const updated = await api.renameLedger(id, name);
    set((state) => ({
      ledgers: state.ledgers.map((ledger) => (ledger.id === id ? updated : ledger)),
    }));
  },

  async updateLedgerSettings(patch) {
    const id = get().currentLedgerId;
    if (!id) {
      return;
    }
    const updated = await api.updateLedger(id, patch);
    set((state) => ({
      ledgers: state.ledgers.map((ledger) => (ledger.id === id ? updated : ledger)),
      context: {
        ...state.context,
        primaryCurrency: updated.currency,
        // Setting a start month jumps the canvas there too.
        ...(patch.startMonth ? { month: patch.startMonth } : {}),
      },
    }));
    if (patch.currency !== undefined) {
      void get().loadRates();
    }
  },

  async setDefaultLedger(id) {
    const { user } = await api.setDefaultLedger(id);
    set({ user });
  },

  async deleteLedger(id) {
    await api.deleteLedger(id);
    const remaining = get().ledgers.filter((ledger) => ledger.id !== id);
    const wasCurrent = get().currentLedgerId === id;
    set({
      ledgers: remaining,
      currentLedgerId: wasCurrent ? (remaining[0]?.id ?? null) : get().currentLedgerId,
    });
    if (wasCurrent) {
      await get().loadGraph();
    }
  },

  moveNode(id, x, y) {
    set((state) => ({
      nodes: state.nodes.map((node) =>
        node.id === id ? { ...node, positionX: x, positionY: y } : node,
      ),
    }));

    const existing = positionTimers.get(id);
    if (existing) {
      clearTimeout(existing);
    }
    positionTimers.set(
      id,
      setTimeout(() => {
        void api.updateNodePosition(id, x, y).catch(() => undefined);
        positionTimers.delete(id);
      }, 400),
    );
  },

  async connect(sourceNodeId, targetNodeId) {
    if (sourceNodeId === targetNodeId) {
      return;
    }
    const source = get().nodes.find((node) => node.id === sourceNodeId);
    const target = get().nodes.find((node) => node.id === targetNodeId);
    if (!source || !target) {
      return;
    }

    // Enforce the connection rules before touching the API.
    const allowed = canConnect(source.type, target.type);
    if (!allowed.ok) {
      get().setNotice(allowed.reason);
      return;
    }

    // A flow out of an income node sweeps the whole income by default.
    const amountType = source.type === "income" ? "remainder" : "fixed";
    try {
      const created = await api.createFlow({
        sourceNodeId,
        targetNodeId,
        amountType,
        amountValue: 0,
        schedule: "on_payday",
        label: "New flow",
      });
      set((state) => ({ flows: [...state.flows, toCoreFlow(created)] }));
    } catch {
      get().setNotice("That connection could not be made.");
    }
  },

  setNotice(notice) {
    set({ notice });
  },

  async removeFlow(id) {
    await api.deleteFlow(id).catch(() => undefined);
    set((state) => ({ flows: state.flows.filter((flow) => flow.id !== id) }));
  },

  setSelection(nodeIds, edgeIds) {
    // Guarded so controlled selection does not loop with React Flow.
    const { selectedNodeIds, selectedEdgeIds } = get();
    const same = (a: string[], b: string[]) =>
      a.length === b.length && a.every((value, index) => value === b[index]);
    if (same(selectedNodeIds, nodeIds) && same(selectedEdgeIds, edgeIds)) {
      return;
    }
    set({ selectedNodeIds: nodeIds, selectedEdgeIds: edgeIds });
  },

  selectNode(id) {
    get().setSelection(id ? [id] : [], []);
  },

  selectEdge(id) {
    get().setSelection([], id ? [id] : []);
  },

  async addNode(type) {
    const { context, nodes, currentLedgerId, ledgers } = get();
    if (!currentLedgerId) {
      return;
    }
    // New nodes adopt the ledger's primary currency by default.
    const ledgerCurrency =
      ledgers.find((ledger) => ledger.id === currentLedgerId)?.currency ?? "GBP";
    // Offset each new node a little so they do not stack exactly on top.
    const offset = nodes.length % 6;
    const created = await api.createNode({
      ledgerId: currentLedgerId,
      type,
      name: defaultName(type),
      currency: ledgerCurrency,
      positionX: 80 + offset * 36,
      positionY: 120 + offset * 36,
      meta: defaultMeta(type, context.month),
    });
    set((state) => ({
      nodes: [...state.nodes, toCoreNode(created)],
      selectedNodeIds: [created.id],
      selectedEdgeIds: [],
    }));
  },

  updateNode(id, patch) {
    // Update local state at once so the engine recomputes instantly, then
    // persist on a short debounce so live typing does not flood the API.
    set((state) => ({
      nodes: state.nodes.map((node) =>
        node.id === id ? ({ ...node, ...patch } as VulpariNode) : node,
      ),
    }));

    const existing = editTimers.get(id);
    if (existing) {
      clearTimeout(existing);
    }
    editTimers.set(
      id,
      setTimeout(() => {
        // Persist the node's full current editable state so edits to different
        // fields in quick succession are all saved.
        const current = get().nodes.find((node) => node.id === id);
        if (current) {
          void api
            .updateNode(id, {
              name: current.name,
              currency: current.currency,
              meta: current.meta,
            })
            .catch(() => undefined);
        }
        editTimers.delete(id);
      }, 500),
    );
  },

  togglePaid(id) {
    const month = get().context.month;
    const node = get().nodes.find((entry) => entry.id === id);
    if (!node) {
      return;
    }
    // Toggle the paid flag for the viewed month on the real (stored) meta.
    const meta = node.meta as { paid?: Record<string, boolean> };
    const paid = { ...(meta.paid ?? {}) };
    if (paid[month]) {
      delete paid[month];
    } else {
      paid[month] = true;
    }
    const cleaned = Object.keys(paid).length > 0 ? paid : undefined;
    get().updateNode(id, { meta: { ...node.meta, paid: cleaned } as VulpariNode["meta"] });
  },

  async deleteNode(id) {
    await get().deleteNodes([id]);
  },

  async deleteNodes(ids) {
    const idSet = new Set(ids);
    await Promise.all(ids.map((id) => api.deleteNode(id).catch(() => undefined)));
    set((state) => ({
      nodes: state.nodes.filter((node) => !idSet.has(node.id)),
      // Flows touching a deleted node are gone server-side; drop them locally too.
      flows: state.flows.filter(
        (flow) => !idSet.has(flow.sourceNodeId) && !idSet.has(flow.targetNodeId),
      ),
      // Drop deleted nodes from their groups, and any group left empty.
      groups: state.groups
        .map((group) => ({
          ...group,
          nodeIds: group.nodeIds.filter((nodeId) => !idSet.has(nodeId)),
        }))
        .filter((group) => group.nodeIds.length > 0),
      selectedNodeIds: state.selectedNodeIds.filter((nodeId) => !idSet.has(nodeId)),
    }));
  },

  async createGroup(name, nodeIds) {
    const { currentLedgerId, groups } = get();
    if (!currentLedgerId || nodeIds.length === 0) {
      return;
    }
    const colour = GROUP_COLOURS[groups.length % GROUP_COLOURS.length]!;
    const created = await api.createGroup(currentLedgerId, name, nodeIds, colour);
    set((state) => ({ groups: [...state.groups, created] }));
  },

  async renameGroup(id, name) {
    const updated = await api.updateGroup(id, { name });
    set((state) => ({ groups: state.groups.map((group) => (group.id === id ? updated : group)) }));
  },

  async recolourGroup(id, color) {
    const updated = await api.updateGroup(id, { color });
    set((state) => ({ groups: state.groups.map((group) => (group.id === id ? updated : group)) }));
  },

  async addToGroup(groupId, nodeIds) {
    const updated = await api.addToGroup(groupId, nodeIds);
    set((state) => ({
      groups: state.groups.map((group) =>
        group.id === groupId
          ? updated
          : // A node belongs to one group, so remove it from any other.
            { ...group, nodeIds: group.nodeIds.filter((id) => !nodeIds.includes(id)) },
      ),
    }));
  },

  async removeFromGroup(nodeId) {
    // Drop the node from whatever group holds it, locally then on the server.
    set((state) => ({
      groups: state.groups.map((group) => ({
        ...group,
        nodeIds: group.nodeIds.filter((id) => id !== nodeId),
      })),
    }));
    await api.updateNode(nodeId, { groupId: null }).catch(() => undefined);
  },

  async toggleCollapse(id) {
    const group = get().groups.find((g) => g.id === id);
    if (!group) {
      return;
    }
    const updated = await api.updateGroup(id, { collapsed: !group.collapsed });
    set((state) => ({ groups: state.groups.map((g) => (g.id === id ? updated : g)) }));
  },

  async ungroup(id) {
    await api.deleteGroup(id).catch(() => undefined);
    set((state) => ({ groups: state.groups.filter((group) => group.id !== id) }));
  },

  setGroupPrompt(prompt) {
    set({ groupPrompt: prompt });
  },

  updateFlow(id, patch) {
    set((state) => ({
      flows: state.flows.map((flow) => (flow.id === id ? { ...flow, ...patch } : flow)),
    }));

    const existing = flowTimers.get(id);
    if (existing) {
      clearTimeout(existing);
    }
    flowTimers.set(
      id,
      setTimeout(() => {
        const current = get().flows.find((flow) => flow.id === id);
        if (current) {
          void api
            .updateFlow(id, {
              amountType: current.amountType,
              amountValue: current.amountValue,
              schedule: current.schedule,
              label: current.label,
            })
            .catch(() => undefined);
        }
        flowTimers.delete(id);
      }, 500),
    );
  },

  setMonth(month) {
    set((state) => ({ context: { ...state.context, month } }));
  },

  setView(view) {
    set({ view });
  },

  setHorizon(horizon) {
    set({ horizon });
  },

  async loadRates() {
    const primary = get().context.primaryCurrency ?? "GBP";
    // Show any cached rates straight away, then refresh in the background.
    const cached = cachedRates(primary);
    if (cached) {
      set((state) => ({ context: { ...state.context, rates: cached } }));
    }
    try {
      const rates = await fetchRates(primary);
      // A ledger switch mid-fetch may have changed the primary currency; only
      // apply if it still matches what we fetched for.
      if ((get().context.primaryCurrency ?? "GBP") === primary) {
        set((state) => ({ context: { ...state.context, rates } }));
      }
    } catch {
      // Offline or the service is down: keep the cache (or nothing). The engine
      // simply leaves unrated currencies out of the primary-currency totals.
    }
  },

  toggleTheme() {
    const theme: Theme = get().theme === "dark" ? "light" : "dark";
    applyTheme(theme);
    set({ theme });
  },

  async updateEmail(email) {
    const { user } = await api.updateEmail(email);
    set({ user });
  },

  async changePassword(currentPassword, newPassword) {
    await api.changePassword(currentPassword, newPassword);
  },
}));

/** The current month as YYYY-MM. */
function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}
